import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  USER_MANUAL_ANCHOR_PREFIX,
  USER_MANUAL_ASSET_BASE_PATH,
  USER_MANUAL_CLOSE_LABEL,
  USER_MANUAL_DOCUMENT_PATH,
  USER_MANUAL_IMAGE_ENLARGE_LABEL,
  USER_MANUAL_IMAGE_OPEN_KEY_ENTER,
  USER_MANUAL_IMAGE_OPEN_KEY_SPACE,
  USER_MANUAL_IMAGE_PREVIEW_MAX_HEIGHT,
  USER_MANUAL_IMAGE_PREVIEW_MAX_WIDTH,
  USER_MANUAL_IMAGE_ZOOM_FACTOR,
  USER_MANUAL_LINK_REL,
  USER_MANUAL_LINK_TARGET,
  USER_MANUAL_LOAD_ERROR,
  USER_MANUAL_PANEL_MAX_WIDTH_PX,
  USER_MANUAL_PANEL_MIN_WIDTH_PX,
  USER_MANUAL_PANEL_TITLE,
  USER_MANUAL_PANEL_WIDTH,
  USER_MANUAL_RELATIVE_PATH_PREFIX,
} from '@constants/user-manual.constants';

interface UserManualPanelProps {
  open: boolean;
  onClose: () => void;
}

interface UserManualImagePreview {
  source: string;
  description: string;
  width: number;
}

function resolveUserManualImageSource(source: string | undefined): string {
  if (!source) return '';
  if (
    source.startsWith('http://') ||
    source.startsWith('https://') ||
    source.startsWith('/')
  ) {
    return source;
  }
  const relativePath = source.startsWith(USER_MANUAL_RELATIVE_PATH_PREFIX)
    ? source.slice(USER_MANUAL_RELATIVE_PATH_PREFIX.length)
    : source;
  return `${USER_MANUAL_ASSET_BASE_PATH}${relativePath}`;
}

function UserManualImagePreviewDialog({
  preview,
  onClose,
}: {
  preview: UserManualImagePreview | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={preview != null}
      onClose={onClose}
      maxWidth={false}
      aria-label={preview?.description || USER_MANUAL_IMAGE_ENLARGE_LABEL}
      PaperProps={{
        sx: {
          width: 'fit-content',
          maxWidth: USER_MANUAL_IMAGE_PREVIEW_MAX_WIDTH,
          maxHeight: USER_MANUAL_IMAGE_PREVIEW_MAX_HEIGHT,
          m: 2,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        },
      }}
    >
      <IconButton
        aria-label={USER_MANUAL_CLOSE_LABEL}
        onClick={onClose}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        <CloseIcon />
      </IconButton>
      {preview && (
        <Box
          component="img"
          src={preview.source}
          alt={preview.description}
          sx={{
            display: 'block',
            width: preview.width,
            maxWidth: USER_MANUAL_IMAGE_PREVIEW_MAX_WIDTH,
            maxHeight: USER_MANUAL_IMAGE_PREVIEW_MAX_HEIGHT,
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      )}
    </Dialog>
  );
}

const UserManualPanel: React.FC<UserManualPanelProps> = ({ open, onClose }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [manualMarkdown, setManualMarkdown] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [imagePreview, setImagePreview] =
    useState<UserManualImagePreview | null>(null);

  useEffect(() => {
    if (!open || manualMarkdown || loadError) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(USER_MANUAL_DOCUMENT_PATH);
        if (!response.ok) throw new Error(USER_MANUAL_LOAD_ERROR);
        const text = await response.text();
        if (!cancelled) setManualMarkdown(text);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, manualMarkdown, loadError]);

  const scrollToAnchor = useCallback((href: string) => {
    const anchorId = href.slice(USER_MANUAL_ANCHOR_PREFIX.length);
    const target = contentRef.current?.querySelector(
      `#${CSS.escape(anchorId)}`
    );
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  let body: React.ReactNode;
  if (loadError) {
    body = (
      <Typography variant="body2" color="error">
        {USER_MANUAL_LOAD_ERROR}
      </Typography>
    );
  } else if (!manualMarkdown) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else {
    body = (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          img: ({ src, alt }) => {
            const source = resolveUserManualImageSource(src);
            const description = alt ?? '';
            const openPreview = (image: HTMLImageElement) => {
              if (!source || image.clientWidth <= 0) return;
              setImagePreview({
                source,
                description,
                width: image.clientWidth * USER_MANUAL_IMAGE_ZOOM_FACTOR,
              });
            };
            return (
              <Box
                component="img"
                src={source}
                alt={description}
                role="button"
                tabIndex={0}
                aria-label={
                  description
                    ? `${USER_MANUAL_IMAGE_ENLARGE_LABEL}: ${description}`
                    : USER_MANUAL_IMAGE_ENLARGE_LABEL
                }
                onClick={(event) => openPreview(event.currentTarget)}
                onKeyDown={(event) => {
                  if (
                    event.key !== USER_MANUAL_IMAGE_OPEN_KEY_ENTER &&
                    event.key !== USER_MANUAL_IMAGE_OPEN_KEY_SPACE
                  ) {
                    return;
                  }
                  event.preventDefault();
                  openPreview(event.currentTarget);
                }}
                sx={{
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  my: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                }}
              />
            );
          },
          a: ({ href, children }) => {
            if (href?.startsWith(USER_MANUAL_ANCHOR_PREFIX)) {
              return (
                <a
                  href={href}
                  onClick={(event) => {
                    event.preventDefault();
                    scrollToAnchor(href);
                  }}
                >
                  {children}
                </a>
              );
            }
            return (
              <a
                href={href}
                target={USER_MANUAL_LINK_TARGET}
                rel={USER_MANUAL_LINK_REL}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {manualMarkdown}
      </ReactMarkdown>
    );
  }

  return (
    <>
      <Drawer
        variant="persistent"
        anchor="right"
        open={open}
        PaperProps={{
          sx: {
            width: USER_MANUAL_PANEL_WIDTH,
            minWidth: USER_MANUAL_PANEL_MIN_WIDTH_PX,
            maxWidth: USER_MANUAL_PANEL_MAX_WIDTH_PX,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <Typography variant="h6">{USER_MANUAL_PANEL_TITLE}</Typography>
          <IconButton
            aria-label={USER_MANUAL_CLOSE_LABEL}
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          ref={contentRef}
          sx={{
            flex: 1,
            minHeight: 0,
            px: 2,
            py: 2,
            overflow: 'auto',
            '& h1': { fontSize: '1.35rem', mt: 0 },
            '& h2': { fontSize: '1.15rem', mt: 3 },
            '& h3': { fontSize: '1rem', mt: 2 },
            '& p, & li': { fontSize: '0.9rem', lineHeight: 1.5 },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              display: 'block',
              overflowX: 'auto',
              fontSize: '0.8rem',
            },
            '& th, & td': {
              border: 1,
              borderColor: 'divider',
              p: 0.75,
              textAlign: 'left',
              verticalAlign: 'top',
            },
          }}
        >
          {body}
        </Box>
      </Drawer>
      <UserManualImagePreviewDialog
        preview={imagePreview}
        onClose={() => setImagePreview(null)}
      />
    </>
  );
};

export default UserManualPanel;
